import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  InputBase,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import { Search, Category, Logout, Refresh } from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import useMenu from "../hooks/useMenu";
import ProductCard from "../components/ProductCard";
import ProductOptionsSheet from "../components/ProductOptionsSheet";
import CategoryPickerModal from "../components/CategoryPickerModal";
import BottomCartBar from "../components/BottomCartBar";
import AutoLoadMore from "../components/AutoLoadMore";

export default function Home() {
  const { store, user, logout } = useAuth();
  const { addItem } = useCart();

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const { loading, loadingMore, error, categories, products, hasMore, loadMoreProducts, reload } = useMenu({ search, category });

  const [activeProduct, setActiveProduct] = useState(null);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);

  const currency = store?.currency || store?.price_type || "";
  const categoryLabel = category === "all" ? "All Categories" : categories.find((c) => c.id === category)?.name || "Category";

  const handleConfirmAdd = ({ product, option, addons, quantity, notes }) => {
    addItem(product, option, addons, quantity, notes);
    setActiveProduct(null);
  };

  return (
    <Box>
      <AppBar position="sticky" color="default" elevation={0} sx={{ bgcolor: "#fff", borderBottom: "1px solid", borderColor: "divider" }}>
        <Toolbar sx={{ gap: 1 }}>
          <Typography
            variant="h6"
            fontWeight={800}
            color="primary.main"
            sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {store?.name || "Denzo"}{user?.name ? ` (${user.name})` : ""}
          </Typography>
          <IconButton onClick={logout} size="small">
            <Logout fontSize="small" />
          </IconButton>
        </Toolbar>
        <Box sx={{ px: 2, pb: 1.5, display: "flex", gap: 1 }}>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              bgcolor: "grey.100",
              borderRadius: 3,
              px: 1.5,
            }}
          >
            <Search fontSize="small" color="disabled" />
            <InputBase placeholder="Search menu" fullWidth sx={{ ml: 1, py: 0.8 }} value={search} onChange={(e) => setSearch(e.target.value)} />
          </Box>
          <Tooltip title={categoryLabel}>
            <IconButton
              onClick={() => setCategoryModalOpen(true)}
              sx={{
                border: "1px solid",
                borderColor: category !== "all" ? "primary.main" : "divider",
                borderRadius: 3,
                bgcolor: category !== "all" ? "primary.main" : "transparent",
                color: category !== "all" ? "primary.contrastText" : "text.primary",
                "&:hover": { bgcolor: category !== "all" ? "primary.dark" : "action.hover" },
              }}
            >
              <Category fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reload menu">
            <IconButton
              onClick={reload}
              disabled={loading}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </AppBar>

      <Box sx={{ p: 2, pb: 12 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : products.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
            No products found.
          </Typography>
        ) : (
          <>
            {products.map((p) => (
              <ProductCard key={p.id} product={p} currency={currency} onPress={setActiveProduct} />
            ))}
            <AutoLoadMore hasMore={hasMore} loadingMore={loadingMore} onLoadMore={loadMoreProducts} />
          </>
        )}
      </Box>

      <ProductOptionsSheet product={activeProduct} currency={currency} onClose={() => setActiveProduct(null)} onConfirm={handleConfirmAdd} />
      <CategoryPickerModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        categories={categories}
        value={category}
        onSelect={setCategory}
      />
      <BottomCartBar currency={currency} />
    </Box>
  );
}
